import { useMemo, useState } from "react";
import { decodeItem } from "../Utils/formatItem";
import { useFieldArray, useForm } from "react-hook-form";
import * as yup from "yup";
import { yupResolver } from "@hookform/resolvers/yup";
import { CallApiCreateItem, CallApiDeleteItem } from "../Api/Enforcement";
import { SwalError, SwalFail, SwalSuccess } from "../Share/Swal";
import { handleGetPass } from "../Share/Func/Storage";

export const useAddItemForm = ({ user, handleCloseKnbModal, items }) => {
  const [isSubmitting, setIsSubmitting] = useState(false);

  const itemDecoded = user.Item ? decodeItem(user.Item) : null;
  const defaultGameItem = useMemo(
    () => ({
      id: items && itemDecoded ? itemDecoded.id : items[0].Id,
      quantity: itemDecoded ? itemDecoded.quantity : 1,
      duration: itemDecoded ? itemDecoded.duration : 0,
      userId: user.AccountName,
      password: handleGetPass(),
    }),
    [user, itemDecoded, items]
  );

  const [selectedType, setSelectedType] = useState(
    itemDecoded
      ? items.find((item) => item.Id === itemDecoded.id)?.TypeId.toString()
      : items[0].TypeId
  );

  const gameItemSchema = yup.object().shape({
    id: yup.number().required(),
    quantity: yup
      .number()
      .typeError("Số lượng không đúng định dạng")
      .required("Vui lòng nhập số lượng")
      .min(1, "Số lượng trong khoảng 1 - 7")
      .max(7, "Số lượng trong khoảng 1 - 7"),
    duration: yup
      .number()
      .typeError("Thời hạn không đúng định dạng")
      .required("Thời hạn nhập số lượng")
      .min(0, "Thời hạn trong khoảng 0 - 30")
      .max(30, "Thời hạn trong khoảng 0 - 30"),
  });

  const addItemSchema = yup.object().shape({
    gameItems: yup.array().of(gameItemSchema).default([]),
  });

  const { control, handleSubmit, setValue } = useForm({
    mode: "onChange",
    reValidateMode: "onChange",
    defaultValues: useMemo(
      () => ({
        gameItems: itemDecoded ? [{ ...defaultGameItem }] : [],
      }),
      [itemDecoded, defaultGameItem]
    ),
    resolver: yupResolver(addItemSchema),
  });

  const { fields, append, remove } = useFieldArray({
    control,
    rules: {
      minLength: 0,
      maxLength: 1,
    },
    name: "gameItems",
  });

  const handleAddGameItem = () => {
    append({
      ...defaultGameItem,
    });
  };

  const handleRemoveGameItem = () => {
    if (isSubmitting) return;
    setIsSubmitting(true);

    CallApiDeleteItem(user.AccountName, (res) => {
      setIsSubmitting(false);
      if (res === "success") {
        SwalSuccess("Cập nhật thành công!");
        handleCloseKnbModal();
      } else {
        var error = res.Status;
        if (error.status === 400) SwalFail(error.data.Message);
        else
          SwalError("Thử tải lại trang!\n Hoặc liên hệ fanpage để được hỗ trợ");
      }
    });
    remove(0);
  };
  const handleAddItem = (formData) => {
    if (isSubmitting) return;
    setIsSubmitting(true);
    CallApiCreateItem(formData.gameItems[0], (res) => {
      setIsSubmitting(false);
      if (!res.Status) {
        if (res !== "success")
          return SwalError("Cập nhật thất bại! \nLiên hệ để được hỗ trợ");
        else {
          SwalSuccess("cập nhật thành công!");
          handleCloseKnbModal();
        }
      } else {
        var error = res.Status;
        if (error.status === 400) SwalFail(error.data.Message);
        else SwalError("Cập nhật thất bại! \nLiên hệ để được hỗ trợ");
      }
    });
  };

  const getUniqueTypes = () => {
    return items.reduce((acc, item) => {
      if (!acc.some((t) => t.value === item.TypeId)) {
        acc.push({ value: item.TypeId, label: item.Type });
      }
      return acc;
    }, []);
  };

  const filterItemsByType = (selectedType) =>
    selectedType
      ? items.filter((item) => item.TypeId === Number(selectedType))
      : items;

  const handleItemChange = (e, onChange) => {
    const item = items.find((item) => item.Id === Number(e.target.value));
    if (item) {
      onChange(item.Id);
      setSelectedType(item.TypeId.toString());
    }
  };

  const handleTypeChange = (e) => {
    setSelectedType(e.target.value);
    var values = items.filter((item) => item.TypeId === Number(e.target.value));
    setValue("gameItems.0.id", values[0].Id);
  };

  return {
    control,
    handleSubmit: handleSubmit(handleAddItem),
    selectedType,
    getUniqueTypes,
    filterItemsByType,
    handleItemChange,
    handleTypeChange,
    gameItems: fields,
    handleAddGameItem,
    handleRemoveGameItem,
  };
};
